import React, { PropsWithoutRef, useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Typography,
  Button,
  Modal,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate } from "react-router-dom";
import { SIDEBAR_WIDTH, STORAGE_KEYS, getFromStorage, removeFromStorage } from "../constants";
import "./Layout.scss";
import { useAppDispatch } from "../hooks/store";
import useAuth from "../hooks/useAuth";
import { generateResponsiveStyle } from "../utils/ModalStyle";
import { useLazyGetUserQuery, useLazyLogoutQuery } from "../services/auth";
import { resetAuth, setCredentials } from "../reducers/authSlice";

interface Props {
  handleSidebarToggle: () => void;
}

function Topbar({ handleSidebarToggle }: PropsWithoutRef<Props>) {
  const navigate = useNavigate();
  const user = useAuth();
  const dispatch = useAppDispatch();
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const style = generateResponsiveStyle();
  const [getUser] = useLazyGetUserQuery();
  const [getLogout] = useLazyLogoutQuery();

  useEffect(() => {
    const token = getFromStorage(STORAGE_KEYS.token);

    if (!token) {
      return;
    }

    dispatch(
      setCredentials({
        user: null,
        token: `${token}`,
      })
    );

    const hydrateUser = async () => {
      try {
        const result = await getUser({}).unwrap();

        if (result?.statusCode === 200) {
          dispatch(
            setCredentials({
              user: result?.data,
              token: `${token}`,
            })
          );
        }
      } catch (error) {
        console.log(error);
      }
    };

    hydrateUser();
  }, [dispatch, getUser]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        document.body.classList.add("is-scrolled");
      } else {
        document.body.classList.remove("is-scrolled");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("is-scrolled");
    };
  }, []);

  const performLocalLogout = () => {
    removeFromStorage(STORAGE_KEYS.token);
    dispatch(resetAuth());
    navigate("/", { replace: true });
  };

  const handleLogout = async () => {
    try {
      await getLogout({}).unwrap();
    } catch (error) {
      console.log(error);
    } finally {
      performLocalLogout();
    }
  };

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigateAndClose = (path: string) => {
    handleClose();
    navigate(path);
  };

  return (
    <Box className="container top_bar">
      <AppBar
        position="fixed"
        className="topbar"
        sx={{
          width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
          ml: { lg: `${SIDEBAR_WIDTH}px` },
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton
            color="inherit"
            aria-label="open sidebar"
            edge="start"
            onClick={handleSidebarToggle}
            className="sidebar_toggle"
            sx={{
              display: { xs: "flex", lg: "none" },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box className="topbar_user_box">
            <IconButton
              className="topbar_user_btn"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <img
                src={
                  user?.image
                    ? user?.image
                    : "/static/images/user_placeholder.png"
                }
                alt="User"
              />
              <span>{`${user?.firstName || "User"} ${user?.lastName || ""}`}</span>
              <KeyboardArrowDownIcon />
            </IconButton>
            <Menu
              className="userDropdown_menu"
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 10px 20px rgba(15, 23, 42, 0.14))",
                  mt: 1.5,
                  "&:before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 12,
                    width: 12,
                    height: 12,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem
                className="userDropdown_menu_list"
                onClick={() => handleNavigateAndClose("/profile")}
              >
                <IconButton className="drpdwn_icns">
                  <PersonIcon />
                </IconButton>
                My Profile
              </MenuItem>
              <MenuItem
                className="userDropdown_menu_list"
                onClick={() => handleNavigateAndClose("/changepassword")}
              >
                <IconButton className="drpdwn_icns">
                  <LockResetIcon />
                </IconButton>
                Change Password
              </MenuItem>

              <MenuItem
                className="userDropdown_menu_list"
                onClick={() => {
                  handleClose();
                  setOpenModal(true);
                }}
              >
                <IconButton className="drpdwn_icns">
                  <LogoutIcon />
                </IconButton>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="logout_mdl"
      >
        <Box sx={style}>
          <Typography
            id="modal-modal-title"
            variant="h6"
            component="h2"
            sx={{ textAlign: "center", fontSize: "20px", color: "#0f172a" }}
          >
            Are you sure you want to logout?
          </Typography>
          <div className="flexdiv">
            <Button
              className="yesBtn"
              sx={{ color: "#0f172a", border: "1px solid #0f172a" }}
              onClick={() => {
                setOpenModal(false);
                handleLogout();
              }}
            >
              Yes
            </Button>

            <Button
              sx={{ color: "#0f172a", border: "1px solid #0f172a" }}
              onClick={() => setOpenModal(false)}
            >
              No
            </Button>
          </div>
        </Box>
      </Modal>
    </Box>
  );
}

export default Topbar;
